import PropTypes from 'prop-types'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { EYEBROW_SX, codeSx } from '../../../generic/qr/qrReadingStyles'
import JobStatusChip from '../FabricationOrderJobReference/PlannedJobs/components/JobStatusChip/JobStatusChip'
import MosSmartDialog from '../common/MosSmartDialog'
import { PENDING_WORK_LOOKUPS } from './qrLookupApi'
import { jobName } from './qrJobText'

/**
 * The notice of a read whose order has nothing to do on this machine (NO_JOB_FOR_DEVICE, RN17, RN29).
 *
 * It answers the operator's three questions in the order they come: this order has no work here
 * (titled with this machine, so the operator knows the label was read where it was meant to be), what
 * its next job is and where it sits, and what to do about it — the large button jumps to that machine,
 * the smaller ones open the order's sheet or just stay.
 *
 * The text follows what Sun answered and nothing else: the next job is the one that gave the
 * destination (`pendingJob`), shown at the destination machine — not at the machine it is planned at,
 * since a job running elsewhere takes the operator where it runs. A Sun with no `pendingJob` gets the
 * line of before amendment 6, and an absent destination means "no pending jobs in the plant". The
 * station's option only decides whether "Go to" is offered, and "View FO card" is offered whenever
 * there is a sheet of the order to open — for a part, only if Sun could build it (RN17a).
 */
const QrTargetDeviceDialog = ({
  open,
  machineName = '',
  orderCode = '',
  partCode = '',
  targetDevice = {},
  pendingJob = {},
  pendingWorkLookup = '',
  offerGoToMachine = true,
  canViewSheet = true,
  onGoToMachine = () => {},
  onViewSheet = () => {},
  onClose
}) => {
  const { t_ } = useSmartTranslation()
  const hasTarget = Boolean(targetDevice.id)
  const hasPendingJob = hasTarget && Boolean(pendingJob.jobReferenceId)
  // Only a Sun that looked and found nothing — or one that does not say, as before — claims the plant has none.
  const lookupFailed = !hasTarget && pendingWorkLookup === PENDING_WORK_LOOKUPS.UNKNOWN
  const pendingJobName = jobName(pendingJob)

  return (
    <MosSmartDialog
      setOpen={open}
      accept={false}
      cancel={false}
      close
      closeCallback={onClose}
      paperWidth='min(32rem, 96vw)'
      title={createDialogTitle({
        color: 'warning',
        title: machineName === ''
          ? t_('This Fabrication Order has no jobs for this machine')
          : t_('This order has no work on {{machine}}', { machine: machineName })
      })}
    >
      <Stack gap={2} sx={{ width: '100%', textAlign: 'left' }}>
        {orderCode !== '' && (
          <Box data-testid='target-order'>
            <Typography sx={codeSx('1rem')}>{orderCode}</Typography>
            {partCode !== '' && (
              <Typography variant='body2' color='text.secondary'>{t_('read from part {{code}}', { code: partCode })}</Typography>
            )}
          </Box>
        )}

        {hasPendingJob && (
          <Stack
            data-testid='pending-job'
            gap={0.75}
            sx={{ p: 2, borderRadius: '0.75rem', border: '1px solid', borderColor: 'divider', backgroundColor: 'action.hover' }}
          >
            <Typography sx={EYEBROW_SX}>{t_('Next job of this order')}</Typography>
            <Typography sx={codeSx('1rem')}>{pendingJob.code}</Typography>
            {pendingJobName !== '' && <Typography>{pendingJobName}</Typography>}
            <Stack direction='row' gap={1} alignItems='center' flexWrap='wrap'>
              <JobStatusChip status={pendingJob.status} statusType={pendingJob.status} />
              <Typography variant='body2' color='text.secondary'>{t_('at {{machine}}', { machine: targetDevice.name })}</Typography>
            </Stack>
          </Stack>
        )}

        {!hasPendingJob && (
          <Typography>
            {hasTarget && t_('Next scheduled job at:') + ' ' + targetDevice.name}
            {!hasTarget && (lookupFailed
              ? t_('The next job of this order could not be found in the current plan')
              : t_('This Fabrication Order has no pending jobs in the plant'))}
          </Typography>
        )}

        <Stack gap={1}>
          {hasTarget && offerGoToMachine && (
            <Button variant='contained' size='large' fullWidth onClick={() => onGoToMachine(targetDevice.id)}>
              {t_('Go to {{machine}}', { machine: targetDevice.name })}
            </Button>
          )}
          <Stack direction='row' gap={1}>
            {canViewSheet && (
              <Button variant='outlined' sx={{ flex: 1 }} onClick={onViewSheet}>{t_('View FO card')}</Button>
            )}
            <Button variant='outlined' color='inherit' sx={{ flex: 1 }} onClick={onClose}>{t_('Stay here')}</Button>
          </Stack>
        </Stack>
      </Stack>
    </MosSmartDialog>
  )
}

QrTargetDeviceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The machine of this screen; '' keeps the title of before amendment 6. */
  machineName: PropTypes.string,
  /** The code of the order that has no work here. */
  orderCode: PropTypes.string,
  /** The code of the part the order was read from; '' when the order itself was read. */
  partCode: PropTypes.string,
  /** The machine Sun suggests; {} when nothing is pending anywhere in the plant. */
  targetDevice: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
  /** The job that gave the destination machine; {} from a Sun older than amendment 6. */
  pendingJob: PropTypes.shape({
    jobReferenceId: PropTypes.number,
    code: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string
  }),
  /** What Sun's search for the next job found (FOUND, NONE, UNKNOWN); '' from a Sun that does not say. */
  pendingWorkLookup: PropTypes.string,
  /** The station's "Button to the right machine". */
  offerGoToMachine: PropTypes.bool,
  /** False for a part whose order sheet Sun could not build. */
  canViewSheet: PropTypes.bool,
  onGoToMachine: PropTypes.func,
  onViewSheet: PropTypes.func,
  /** Closes the notice: the dialog's cross and "Stay here". */
  onClose: PropTypes.func.isRequired
}

export default QrTargetDeviceDialog
